import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { Facebook, Instagram, LinkedIn } from '@mui/icons-material';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import chandlerImage from '../../assets/images/chandler_image.png';
import lukeImage from '../../assets/images/luke_image_2.jpeg';
import HowItWorks from '../HowItWorks/HowItWorks';
import Pricing from '../Pricing/Pricing';
import SEO from '../common/SEO/SEO';
import {
  CardText,
  CardTitle,
  ContactLine,
  DifferenceCard,
  DifferenceGrid,
  Eyebrow,
  FounderBio,
  FounderCard,
  FounderGrid,
  FounderHeader,
  FounderImage,
  FounderName,
  FounderRole,
  IconChip,
  IntroCopy,
  IntroHeading,
  IntroLead,
  IntroPanel,
  Section,
  SectionTitle,
  SocialLinks,
  StoryCard,
} from './AboutUs.styled';

const DIFFERENCES = [
  {
    Icon: MoneyOffRoundedIcon,
    title: 'No hidden fees',
    text: 'Homeowners never pay commissions or platform charges. Just hire the pro you choose.',
  },
  {
    Icon: LocationOnRoundedIcon,
    title: 'Built for NKY',
    text: 'Only Northern Kentucky businesses. No national spam, no cold outreach lists.',
  },
  {
    Icon: VerifiedUserRoundedIcon,
    title: 'Trusted providers',
    text: 'We highlight reputable pros and real reviews so you can hire with confidence.',
  },
  {
    Icon: AutoAwesomeRoundedIcon,
    title: 'Smarter matching',
    text: 'Tell us the job and we surface the best-fit pros faster, without the guesswork.',
  },
];

const FOUNDERS = [
  {
    name: 'Chandler Farmer',
    image: chandlerImage,
    bio:
      "Chandler is a Northern Kentucky native and Campbell County High School graduate who earned his MBA from NKU, where he also earned his bachelor's in Cybersecurity in 2023. Chandler works as an API Product Manager at U.S. Bank, where he builds corporate APIs for treasury, payments, and money movement. He's passionate about using technology to simplify systems and support local business growth.",
    socials: {
      facebook: 'https://www.facebook.com/chandler.farmer.75',
      instagram: 'https://instagram.com/chandler.farmer',
      linkedin: 'https://www.linkedin.com/in/chandlerfarmer/',
    },
  },
  {
    name: 'Luke Williams',
    image: lukeImage,
    bio:
      "Luke is a Northern Kentucky native and Campbell County High School graduate who earned his Bachelor's in Computer Science with a minor in Mathematics from the University of Kentucky in 2023. Currently working as a software engineer at 84.51°, he helps major brands and CPGs optimize their supply chain management through Kroger's extensive data ecosystem. Luke's passion lies in leveraging his software development expertise to create meaningful impacts on people's lives.",
    socials: {
      facebook: 'https://www.facebook.com/profile.php?id=100026786476544',
      instagram: 'https://instagram.com/ltw10',
      linkedin: 'https://www.linkedin.com/in/luke-williams-t102701/',
    },
  },
];

const SOCIAL_ICONS = [
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'linkedin', label: 'LinkedIn', Icon: LinkedIn },
];

export const AboutUs = () => (
  <>
    <SEO
      title="About Us"
      description="Learn how NKY Connect helps Northern Kentucky residents find trusted local home service providers — and how we support local businesses."
      path="/about"
    />

    <IntroPanel aria-labelledby="about-title">
      <IntroCopy>
        <Eyebrow>About us</Eyebrow>
        <IntroHeading id="about-title">What is NKY Connect?</IntroHeading>
        <IntroLead>
          The easiest way to find, compare, and hire trusted home service pros in Northern
          Kentucky.
        </IntroLead>
      </IntroCopy>
      <StoryCard>
        Two NKY locals who couldn&apos;t find a reliable pro, so they built the solution. NKY
        Connect started because finding trusted home service providers shouldn&apos;t mean
        guessing from random ads or national directory spam. You deserve neighbors you can vet,
        reviews you can trust, and a path to reach the right pro the first time.
      </StoryCard>
    </IntroPanel>

    <Section aria-labelledby="different-title">
      <SectionTitle id="different-title">How we&apos;re different</SectionTitle>
      <DifferenceGrid>
        {DIFFERENCES.map(({ Icon, title, text }) => (
          <DifferenceCard key={title}>
            <IconChip aria-hidden>
              <Icon />
            </IconChip>
            <CardTitle>{title}</CardTitle>
            <CardText>{text}</CardText>
          </DifferenceCard>
        ))}
      </DifferenceGrid>
    </Section>

    <HowItWorks />

    <Section id="founders" aria-labelledby="founders-title">
      <SectionTitle id="founders-title">Meet the founders</SectionTitle>
      <FounderGrid>
        {FOUNDERS.map(({ name, image, bio, socials }) => (
          <FounderCard key={name}>
            <FounderHeader>
              <FounderImage src={image} alt={name} />
              <div>
                <FounderName>{name}</FounderName>
                <FounderRole>Co-Founder</FounderRole>
                <SocialLinks>
                  {SOCIAL_ICONS.map(({ key, label, Icon }) => (
                    <IconButton
                      key={key}
                      size="small"
                      href={socials[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${name} on ${label}`}
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  ))}
                </SocialLinks>
              </div>
            </FounderHeader>
            <FounderBio>{bio}</FounderBio>
          </FounderCard>
        ))}
      </FounderGrid>
    </Section>

    <Pricing />

    <ContactLine>
      Have questions? <RouterLink to="/contact">Contact us</RouterLink>
    </ContactLine>
  </>
);
